import { env } from '@pass-frontend/features/configuration/util';

const {
  HSID_URL,
  HSID_AIKYAM_URL,
  HSID_PING_URL,
  HSID_CLIENT_ID,
  HSID_PFIDP_ADAPTER_ID,
  HSID_PORTAL_SHORTNAME,
  HSID_CALLBACK_REDIRECT_URI,
  OHID_PING_URL,
  OHID_CLIENT_ID,
  OHID_CALLBACK_REDIRECT_URI,
  CLIENT_REPORT_BASE_PATH,
  FF_ENABLE_COMMERCIAL_USING_HSID_EDA,
  HSID_EDA_URL,
  HSID_EDA_COMMERCIAL_CLIENT_ID,
  HSID_EDA_COMMERCIAL_REDIRECT_URI,
  PASS_STANDALONE_COMMERCIAL_URL,
  PASS_STANDALONE_GOVERNMENT_URL,
  PASS_CORPORATE_URL,
} = env();

let serviceSector, isSubtenant;

const origin = typeof window !== 'undefined' && window.location.origin;
const pathname = typeof window !== 'undefined' && window.location.pathname;
if (typeof origin === 'string') {
  isSubtenant =
    origin.match(/-pr-(\d{4,})/) ||
    (typeof pathname === 'string' && pathname.match(/one-pass-pr-(\d{4,})/));
  // for local development with commercial configuration
  // running government under localhost:3001 or anything other than :3000 will be considered as government
  serviceSector =
    origin.includes('commercial') ||
    origin.includes('onepassselect') ||
    // this is for the new domain https://select.dev.one-pass-dev.com, htts://select.uat.one-pass-dev.com, and https://select.one-pass.com.
    (origin.includes('select') && origin.includes('one-pass')) ||
    origin.includes('localhost:3000')
      ? 'commercial'
      : 'government';
}

const callbackURI = isSubtenant
  ? `/rest/pass-edge-pr-${isSubtenant[1]}/hsid/${serviceSector}/callback`
  : `${HSID_CALLBACK_REDIRECT_URI}`;

const ohidCallbackURI = isSubtenant
  ? `/rest/pass-edge-pr-${isSubtenant[1]}/ohid/client/callback`
  : `${OHID_CALLBACK_REDIRECT_URI}`;

const isCommercialEda =
  FF_ENABLE_COMMERCIAL_USING_HSID_EDA && serviceSector === 'commercial';

export const getLoginURL = (locale = 'en') => {
  return isCommercialEda
    ? `${HSID_EDA_URL}/oidc/authorize?client_id=${HSID_EDA_COMMERCIAL_CLIENT_ID}&response_type=code&scope=openid+profile+email+address&redirect_uri=${HSID_EDA_COMMERCIAL_REDIRECT_URI}&ui_locales=${locale}`
    : `${HSID_PING_URL}/ext/as/authorization.oauth2?client_id=${HSID_CLIENT_ID}&pfidpadapterid=${HSID_PFIDP_ADAPTER_ID}&response_type=code&scope=openid+profile&redirect_uri=${origin}${callbackURI}&portal=${HSID_PORTAL_SHORTNAME}`;
};

export const getRegistrationUrl = (locale = 'en') => {
  return isCommercialEda
    ? `${HSID_EDA_URL}/oidc/authorize?client_id=${HSID_EDA_COMMERCIAL_CLIENT_ID}&response_type=code&scope=openid+profile+email+address&redirect_uri=${HSID_EDA_COMMERCIAL_REDIRECT_URI}&ui_locales=${locale}`
    : `${HSID_URL}/rt/register/${HSID_PORTAL_SHORTNAME}/${locale} `;
};

export const REGISTRATION_PATH_FOR_SPLASH_PAGE = (portalShortName: string) =>
  `${HSID_URL}/rt/register/${portalShortName}/en`;
export const LOGIN_PATH_FOR_SPLASH_PAGE = (
  portalWithSponsor: PortalWithSponsor,
) =>
  `${HSID_PING_URL}/ext/as/authorization.oauth2?client_id=${portalWithSponsor.clientId}&pfidpadapterid=${HSID_PFIDP_ADAPTER_ID}&response_type=code&scope=openid+profile&redirect_uri=${portalWithSponsor.callbackUrl}&portal=${portalWithSponsor.portalShortName}`;

export const LOGOUT_URL = `${HSID_URL}/content/en/healthsafeid/public/logout.html?HTTP_TARGETPORTAL=${HSID_PORTAL_SHORTNAME}&HTTP_TARGETURL=${origin}/api/logout`;

export const getAssumedEligibleLogoutURL = (idToken: string | undefined) => {
  return `${HSID_AIKYAM_URL}/oidc/logout?id_token_hint=${idToken}&post_logout_redirect_uri=${origin}/api/logout`;
};
export const getCommercialEdaLogoutURL = (idToken: string | undefined) => {
  return `${HSID_EDA_URL}/oidc/logout?id_token_hint=${idToken}&post_logout_redirect_uri=${origin}/api/logout`;
};
const getMemberTargetUrl = (locale: string) =>
  `${origin}${locale === 'en' ? '' : `/${locale}`}/member`;
export const SETTINGS_HSID = (locale = 'en') =>
  isCommercialEda
    ? `${HSID_EDA_URL}/web/redirect?to=manage-profile&target=${getMemberTargetUrl(locale)}&post_logout_redirect_uri=${origin}/api/logout&ui_locales=${locale}`
    : `${HSID_URL}/rt/secure/settings/${HSID_PORTAL_SHORTNAME}`;
export const OHID_LOGIN_PATH = `${OHID_PING_URL}/oidc/authorize?client_id=${OHID_CLIENT_ID}&response_type=code&scope=openid+profile&redirect_uri=${origin}${ohidCallbackURI}`;
export const CLIENT_REPORT_PATH = `${CLIENT_REPORT_BASE_PATH}/trusted/{trustedToken}/t/Fitness/views/LandingPage/LandingPage?iframeSizedToWindow=true&%3Aembed=y&%3AshowAppBanner=false&%3Atoolbar=no`;

export interface PortalWithSponsor {
  clientId: string;
  callbackUrl: string;
  portalShortName: string;
}

export const SOCIAL_TILE_EXTERNAL_LINK = `https://hellogrouper.com/welcome-to-grouper`;

export const SOCIAL_TILE_EXTERNAL_LINK_GET_ACTIVE = `https://hellogrouper.com/welcome-to-grouper`;

export const ONEPASS_IOS_APP_LINK = 'https://oadhoc.sc-fwd.com/VOjiao';

export const ONEPASSCORP_IOS_APP_LINK = 'https://one-pass.com/link/appstore';

export const ONEPASS_ANDROID_APP_LINK = 'https://oadhoc.sc-fwd.com/1wuZub';

export const ONEPASSCORP_ANDROID_APP_LINK =
  'https://one-pass.com/link/googleplay';

export const ONEPASS_CORPORATE_WEBSITE_LINK = PASS_CORPORATE_URL;

export const MEDIA_INQUIRIES_EMAIL = 'mailto:opmedia@one-pass.com';

export const REQUEST_DEMO_LINK = `${PASS_CORPORATE_URL}/contact-us?sf`;

export const CAREERS_LINK = `${PASS_CORPORATE_URL}/careers`;

export const TERMS_COMMERCIAL = `${PASS_STANDALONE_COMMERCIAL_URL}/terms-of-use`;

export const TERMS_GOVERNMENT = `${PASS_STANDALONE_GOVERNMENT_URL}/terms-of-use`;

export const getTermsAndPrivacyOnePassCommercialUrl = (locale = 'en') =>
  `${HSID_EDA_URL}/oneapp/index.html#/legal?portal=onepassselect_eda&locale=${locale}&theme=Optum`;

export const getTermsAndPrivacyHsidCommercialUrl = (locale = 'en') =>
  `${HSID_EDA_URL}/oneapp/index.html#/legal?theme=optum&locale=${locale}`;

export const PRIVACY_POLICY_COMMERCIAL = `${PASS_STANDALONE_COMMERCIAL_URL}/privacy-policy`;

export const PRIVACY_POLICY_GOVERNMENT = `${PASS_STANDALONE_GOVERNMENT_URL}/privacy-policy`;
